import {
  Box,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Tabs,
  Tab,
  useTheme,
} from "@mui/material";
import { useState } from "react";

const mockNotifications = [
  {
    id: 1,
    title: "Server Down",
    description: "Production server is not responding",
    time: new Date(),
    isRead: false,
  },
  {
    id: 2,
    title: "New Signup",
    description: "A new user registered",
    time: new Date(Date.now() - 86400000),
    isRead: false,
  },
  {
    id: 3,
    title: "Invoice Paid",
    description: "Invoice #1234 has been paid",
    time: new Date(Date.now() - 2 * 86400000),
    isRead: true,
  },
  {
    id: 4,
    title: "New Ticket",
    description: "Ticket #456 created by Jay Mani",
    time: new Date(),
    isRead: false,
  },
  {
    id: 5,
    title: "Backup Complete",
    description: "Daily backup finished successfully",
    time: new Date(Date.now() - 86400000),
    isRead: true,
  },
  {
    id: 6,
    title: "System Update",
    description: "New security patches installed",
    time: new Date(Date.now() - 3 * 86400000),
    isRead: false,
  },
];

function isToday(date) {
  const today = new Date();
  return (
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
  );
}

function isYesterday(date) {
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  return (
    date.getDate() === yesterday.getDate() &&
    date.getMonth() === yesterday.getMonth() &&
    date.getFullYear() === yesterday.getFullYear()
  );
}

export default function NotificationPanel() {
  const theme = useTheme();
  const [notifications, setNotifications] = useState(mockNotifications);
  const [visibleCount, setVisibleCount] = useState(4);
  const [tab, setTab] = useState("all");

  const handleMarkAllRead = () => {
    setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
  };

  const handleClearAll = () => {
    setNotifications([]);
    setVisibleCount(0);
  };

  const grouped = {
    today: [],
    yesterday: [],
    earlier: [],
  };

  const filtered = notifications.filter((n) => {
    if (tab === "unread") return !n.isRead;
    if (tab === "read") return n.isRead;
    return true;
  });

  filtered.slice(0, visibleCount).forEach((n) => {
    if (isToday(n.time)) grouped.today.push(n);
    else if (isYesterday(n.time)) grouped.yesterday.push(n);
    else grouped.earlier.push(n);
  });

  const renderGroup = (label, items) => {
    if (items.length === 0) return null;
    return (
      <Box key={label}>
        <Typography
          variant="subtitle2"
          color="text.secondary"
          sx={{ mt: 1, mb: 1 }}
        >
          {label}
        </Typography>
        {items.map((n) => (
          <Box key={n.id}>
            <ListItem
              alignItems="flex-start"
              sx={{
                bgcolor: n.isRead
                  ? theme.palette.background.paper
                  : theme.palette.mode === "dark"
                    ? "rgba(66, 165, 245, 0.2)"
                    : "#e3f2fd",
                borderRadius: 1,
                border: `1px solid ${
                  n.isRead ? theme.palette.divider : theme.palette.primary.light
                }`,
                mb: 1,
              }}
            >
              <ListItemAvatar>
                <Avatar>{n.title[0]}</Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={n.title}
                secondary={n.description}
                primaryTypographyProps={{
                  fontWeight: n.isRead ? "normal" : "bold",
                }}
              />
            </ListItem>
          </Box>
        ))}
      </Box>
    );
  };

  return (
    <Box
      sx={{
        width: 420,
        p: 2,
        bgcolor: theme.palette.background.default,
        color: theme.palette.text.primary,
        borderRadius: 2,
        boxShadow: 3,
        border: `1px solid ${
          theme.palette.mode === "dark"
            ? theme.palette.grey[700]
            : theme.palette.grey[300]
        }`,
      }}
    >
      {/* Header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={1}
      >
        <Typography variant="h6">Notifications</Typography>
        <Box>
          <Button size="small" onClick={handleMarkAllRead}>
            Mark all as read
          </Button>
          <Button size="small" color="error" onClick={handleClearAll}>
            Clear All
          </Button>
        </Box>
      </Box>

      {/* Tabs */}
      <Tabs
        value={tab}
        onChange={(e, newValue) => setTab(newValue)}
        variant="fullWidth"
        size="small"
      >
        <Tab value="all" label="All" />
        <Tab value="unread" label="Unread" />
        <Tab value="read" label="Read" />
      </Tabs>

      {/* Scrollable Notification List */}
      <Box
        sx={{
          maxHeight: 400,
          overflowY: "auto",
          pr: 1,
          mt: 1,
        }}
      >
        <List disablePadding>
          {grouped.today.length === 0 &&
          grouped.yesterday.length === 0 &&
          grouped.earlier.length === 0 ? (
            <Box sx={{ py: 5, textAlign: "center", color: "text.secondary" }}>
              <Typography variant="body1">No notifications</Typography>
            </Box>
          ) : (
            <>
              {renderGroup("Today", grouped.today)}
              {renderGroup("Yesterday", grouped.yesterday)}
              {renderGroup("Earlier", grouped.earlier)}
            </>
          )}
        </List>
      </Box>

      {/* Show More */}
      {visibleCount < filtered.length && (
        <Box textAlign="center" mt={1}>
          <Button
            size="small"
            variant="outlined"
            onClick={() => setVisibleCount((prev) => prev + 3)}
          >
            Show More
          </Button>
        </Box>
      )}
    </Box>
  );
}
